import { Button, EmptyState, Skeleton } from '@westhetics/ui'
import { LoadError } from '@/app/load-error'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { AnamnesisReading } from './anamnesis-reading'
import { AnamnesisRecord } from './anamnesis-record'
import { useAnamnesis } from './api'

/**
 * A anamnese aberta dentro da aba da ficha (`GET /anamneses/:id`): respondida, a leitura; não
 * preenchida ou cancelada, o registro do envio. Quem não pode ler a respondida (recepção, prestadora
 * que não é a responsável) recebe 403 da API e vê por quê, com a volta para a lista.
 */

/** O esqueleto com a forma da leitura: cabeçalho de prova, bloco de atenção e pares. */
function DetailSkeleton() {
  return (
    <div role="status" aria-label="Carregando a anamnese" className="flex flex-col gap-4">
      <Skeleton className="h-[13px] w-[120px]" />
      <Skeleton className="h-[18px] w-[220px]" />
      <Skeleton className="h-[46px] w-full" />
      <Skeleton className="h-[96px] w-full" />
      <div className="tablet:grid-cols-2 grid grid-cols-1 gap-x-6 gap-y-5">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="h-[11px] w-[140px]" />
            <Skeleton className="h-[15px] w-[220px] max-w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function AnamnesisDetail({
  anamnesisId,
  patientId,
  onBack,
}: {
  anamnesisId: string
  /** A paciente da ficha: anamnese de outra pessoa não abre aqui. */
  patientId: string
  onBack: () => void
}) {
  const query = useAnamnesis(anamnesisId)
  const back = (
    <Button variant="outline" onClick={onBack}>
      Voltar à lista
    </Button>
  )

  if (query.isPending) return <DetailSkeleton />

  if (query.isError) {
    const error = query.error
    if (isApiError(error) && error.code === 'forbidden') {
      return (
        <EmptyState
          title="Leitura disponível para a profissional responsável e a gestora"
          description={errorMessage(error)}
          action={back}
        />
      )
    }
    if (isApiError(error) && error.code === 'not_found') {
      return (
        <EmptyState
          title="Anamnese não encontrada"
          description="Ela não existe nesta clínica, ou o endereço está incompleto. As anamneses desta paciente estão na lista."
          action={back}
        />
      )
    }
    return (
      <LoadError
        title="Não conseguimos abrir esta anamnese"
        error={error}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const anamnesis = query.data
  if (anamnesis.patient.id !== patientId) {
    return (
      <EmptyState
        title="Esta anamnese é de outra paciente"
        description="O endereço aponta para uma anamnese que não pertence a esta ficha. As anamneses desta paciente estão na lista."
        action={back}
      />
    )
  }

  return anamnesis.status === 'completed' ? (
    <AnamnesisReading anamnesis={anamnesis} onBack={onBack} />
  ) : (
    <AnamnesisRecord anamnesis={anamnesis} onBack={onBack} />
  )
}
